import * as THREE from 'three';

/**
 * ItemDropSystem — Administra items sueltos en el mundo.
 *
 * Cuando el jugador muere, sus items caen en la posicion de muerte.
 * Los items flotan y giran, se pueden recoger caminando cerca.
 * Los items desaparecen despues de 5 minutos.
 */

const PICKUP_RADIUS = 1.5;
const DESPAWN_TIME = 300; // 5 minutos en segundos
const BOB_SPEED = 2.5;   // velocidad de oscilacion
const BOB_HEIGHT = 0.15;  // amplitud de oscilacion
const SPIN_SPEED = 1.8;   // velocidad de rotacion (rad/s)
const BOX_SIZE = 0.3;

export class ItemDropSystem {
  /**
   * @param {THREE.Scene} scene — la escena de Three.js
   */
  constructor(scene) {
    this.scene = scene;
    /** @type {Array<{group: THREE.Group, mesh: THREE.Mesh, light: THREE.PointLight, baseY: number, items: Array, createdAt: number, bobPhase: number}>} */
    this.drops = [];
  }

  /**
   * Suelta items en una posicion del mundo (ej. al morir el jugador).
   * @param {{x: number, y: number, z: number}} position — posicion de muerte
   * @param {Array<{id: number, count: number}>} items — lista de items a soltar
   */
  dropItems(position, items) {
    if (!items || items.length === 0) return;

    // Agrupar todos los items en un solo drop entity
    const group = new THREE.Group();
    group.position.set(position.x, position.y + 0.5, position.z);

    // Cubo blanco brillante con emissive glow
    const geometry = new THREE.BoxGeometry(BOX_SIZE, BOX_SIZE, BOX_SIZE);
    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0x88ccff,
      emissiveIntensity: 0.8,
      roughness: 0.3,
      metalness: 0.1,
    });
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);

    // Luz puntual para efecto de brillo
    const light = new THREE.PointLight(0x88ccff, 0.6, 4);
    light.position.set(0, 0.2, 0);
    group.add(light);

    this.scene.add(group);

    const drop = {
      group,
      mesh,
      light,
      baseY: position.y + 0.5,
      items: items.map(it => ({ ...it })),
      createdAt: performance.now() / 1000,
      bobPhase: Math.random() * Math.PI * 2,
    };

    this.drops.push(drop);
  }

  /**
   * Actualiza todos los drops: animacion de flotacion, despawn, y recogida.
   * @param {number} dt — delta time en segundos
   * @param {{x: number, y: number, z: number}} playerPos — posicion del jugador
   * @returns {Array<{id: number, count: number}>|null} — items recogidos, o null
   */
  update(dt, playerPos) {
    const now = performance.now() / 1000;
    let pickedUpItems = null;

    for (let i = this.drops.length - 1; i >= 0; i--) {
      const drop = this.drops[i];

      // --- Despawn por tiempo ---
      if (now - drop.createdAt > DESPAWN_TIME) {
        this._removeDrop(i);
        continue;
      }

      // --- Animacion: bobbing + rotacion ---
      drop.bobPhase += dt * BOB_SPEED;
      drop.group.position.y = drop.baseY + Math.sin(drop.bobPhase) * BOB_HEIGHT;
      drop.mesh.rotation.y += dt * SPIN_SPEED;

      // Pulso suave en la luz
      const pulse = 0.4 + 0.2 * Math.sin(drop.bobPhase * 1.5);
      drop.light.intensity = pulse;

      // --- Recogida por proximidad del jugador ---
      if (playerPos) {
        const dx = drop.group.position.x - playerPos.x;
        const dy = drop.group.position.y - playerPos.y;
        const dz = drop.group.position.z - playerPos.z;
        const distSq = dx * dx + dy * dy + dz * dz;

        if (distSq <= PICKUP_RADIUS * PICKUP_RADIUS) {
          // Recoger este drop
          if (!pickedUpItems) pickedUpItems = [];
          pickedUpItems.push(...drop.items);
          this._removeDrop(i);
        }
      }
    }

    return pickedUpItems;
  }

  /**
   * Elimina un drop del mundo y de la lista.
   * @param {number} index
   */
  _removeDrop(index) {
    const drop = this.drops[index];

    // Limpiar geometria y material
    drop.mesh.geometry.dispose();
    drop.mesh.material.dispose();

    // Sacar del scene
    this.scene.remove(drop.group);

    // Sacar de la lista
    this.drops.splice(index, 1);
  }

  /**
   * Elimina todos los drops del mundo.
   */
  clear() {
    for (let i = this.drops.length - 1; i >= 0; i--) {
      this._removeDrop(i);
    }
  }

  /**
   * Serializa los drops para guardar partida.
   * @returns {Array}
   */
  serialize() {
    const now = performance.now() / 1000;
    return this.drops.map(drop => ({
      position: {
        x: drop.group.position.x,
        y: drop.baseY,
        z: drop.group.position.z,
      },
      items: drop.items.map(it => ({ ...it })),
      age: now - drop.createdAt,
    }));
  }

  /**
   * Restaura drops desde datos guardados.
   * @param {Array} data — datos serializados
   */
  deserialize(data) {
    if (!data || !Array.isArray(data)) return;

    const now = performance.now() / 1000;

    for (const entry of data) {
      // Descartar drops que ya habrian expirado
      if (entry.age >= DESPAWN_TIME) continue;

      // Recrear el drop
      this.dropItems(entry.position, entry.items);

      // Ajustar el tiempo de creacion para respetar la edad guardada
      const drop = this.drops[this.drops.length - 1];
      if (drop) {
        drop.createdAt = now - entry.age;
      }
    }
  }
}
